import { useEffect, useState, type FormEvent } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Check, Diamond, Pencil, Plus } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import {
  HEALTH_LABEL,
  isOverdue,
  trackerKey,
  useTracker,
  type HealthLevel,
  type Task,
  type TrackerData,
  type TrackerPhase,
} from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { Avatar, DueDate, HealthBadge, ProgressRing, StatusPill } from '@/components/tracker/bits';
import { useOpenTask } from '@/components/tracker/TaskPanel';
import { PhaseDocuments } from '@/components/tracker/PhaseDocuments';
import { useRaid } from '@/lib/raid';
import { RaidTable } from '@/components/raid/RaidTable';
import { ProgressReview } from '@/components/ai/Assistant';
import { deliverableLabel, type DeliverableStatus } from '@/lib/deliverables';

const TABS = ['tasks', 'deliverables', 'documents', 'raid', 'notes'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABEL: Record<Tab, string> = {
  tasks: 'Tasks',
  deliverables: 'Deliverables',
  documents: 'Documents',
  raid: 'RAID',
  notes: 'Notes',
};

export function PhasePage() {
  const program = useOutletContext<Program>();
  const { phaseId } = useParams();
  const tracker = useTracker(program.id);
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'tasks';
  const isAdmin = program.capabilities.includes('plan.manage');

  if (!tracker.data) return <Spinner className="h-full" />;
  const data = tracker.data;
  const phase = data.phases.find((p) => p.id === phaseId);
  if (!phase) return <p className="p-8 text-sm text-muted">That phase does not exist.</p>;
  const tasks = data.tasks.filter((t) => t.phaseId === phase.id);
  const deliverables = data.deliverables.filter((d) => d.phaseId === phase.id);
  const slip = phase.forecastEnd > phase.plannedEnd;

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-5">
      <Link
        to={`/p/${program.id}/tracker`}
        className="mb-3 inline-flex items-center gap-1 text-xs text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" /> Tracker
      </Link>

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">Phase {phase.number}</p>
          <h1 className="mt-0.5 text-xl font-semibold tracking-tight">{phase.name}</h1>
          <p className="mt-1.5 text-sm text-muted">{phase.objective}</p>
          <div className="mt-3">
            <ProgressReview program={program} phaseId={phase.id} label="Review this phase" />
          </div>
          {phase.windowNote && (
            <Alert tone="warn" className="mt-3">
              {phase.windowNote}
            </Alert>
          )}
        </div>
        <Card className="flex items-center gap-4 p-4">
          <ProgressRing done={phase.taskProgress.done} total={phase.taskProgress.total} size={64} />
          <div className="text-[13px]">
            <p>
              <span className="font-semibold tabular">{phase.taskProgress.done}</span> of {phase.taskProgress.total}{' '}
              tasks done
            </p>
            <p className="mt-0.5">
              <span className="font-semibold tabular">{phase.deliverableProgress.done}</span> of{' '}
              {phase.deliverableProgress.total} deliverables accepted
            </p>
            <div className="mt-1.5">
              <HealthBadge health={phase.health} />
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <DatesCard program={program} phase={phase} canEdit={isAdmin} slip={slip} />
        <HealthCard program={program} phase={phase} canEdit={isAdmin} />
        <MilestonesCard program={program} phase={phase} today={data.today} canEdit={isAdmin} />
      </div>

      <div className="mt-6 flex gap-1 border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (t === 'tasks') next.delete('tab');
              else next.set('tab', t);
              setParams(next, { replace: true });
            }}
            className={cn(
              '-mb-px border-b-2 px-3 pb-2.5 pt-1 text-sm',
              tab === t ? 'border-brand font-medium' : 'border-transparent text-muted hover:text-text',
            )}
          >
            {TAB_LABEL[t]}
            {t === 'tasks' && <span className="ml-1 text-xs text-subtle">{tasks.length}</span>}
            {t === 'deliverables' && <span className="ml-1 text-xs text-subtle">{deliverables.length}</span>}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {tab === 'tasks' && <PhaseTasks phase={phase} tasks={tasks} data={data} />}
        {tab === 'deliverables' && (
          <Card>
            <ul>
              {deliverables.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center gap-3 border-b border-border px-4 py-2.5 text-[13px] last:border-0"
                >
                  <Link to={`/p/${program.id}/deliverables/${d.id}`} className="flex-1 truncate hover:underline">
                    {d.name}
                  </Link>
                  {d.isProgramLevel && <Badge tone="brand">PA §4</Badge>}
                  <span className="w-36 text-xs text-muted">
                    {deliverableLabel(d.status as DeliverableStatus, program.clientShortName)}
                  </span>
                  <span className="w-28 text-right text-xs tabular text-muted">
                    {d.targetDate ? fmtDate(d.targetDate) : 'No target'}
                  </span>
                </li>
              ))}
              {!deliverables.length && (
                <li className="px-4 py-6 text-center text-sm text-muted">No deliverables in this phase.</li>
              )}
            </ul>
          </Card>
        )}
        {tab === 'documents' && <PhaseDocuments program={program} phaseId={phase.id} />}
        {tab === 'raid' && <PhaseRaid program={program} phaseId={phase.id} />}
        {tab === 'notes' && <PhaseNotes program={program} phase={phase} canEdit={isAdmin} />}
      </div>
    </div>
  );
}

function usePhaseSave(program: Program, phaseId: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.patch(`/api/programs/${program.id}/phases/${phaseId}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKey(program.id) }),
    onError: (e) => toast.error(errorMessage(e)),
  });
}

function DatesCard({
  program,
  phase,
  canEdit,
  slip,
}: {
  program: Program;
  phase: TrackerPhase;
  canEdit: boolean;
  slip: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const save = usePhaseSave(program, phase.id);
  return (
    <Card className="p-4 text-[13px]">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Dates</h3>
        {canEdit && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil /> Reforecast
          </Button>
        )}
      </div>
      <p className="text-muted">
        PA window{' '}
        <span className="tabular text-text">
          {fmtDate(phase.plannedStart)} to {fmtDate(phase.plannedEnd)}
        </span>
      </p>
      {editing ? (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            save.mutate({ forecastStart: f.get('s'), forecastEnd: f.get('e') }, { onSuccess: () => setEditing(false) });
          }}
        >
          <div className="flex gap-2">
            <Input
              name="s"
              type="date"
              aria-label="Forecast start"
              defaultValue={phase.forecastStart}
              className="h-8 text-[13px]"
            />
            <Input
              name="e"
              type="date"
              aria-label="Forecast end"
              defaultValue={phase.forecastEnd}
              className="h-8 text-[13px]"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" loading={save.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-1 text-muted">
          Forecast{' '}
          <span className={cn('tabular', slip ? 'font-medium text-warn' : 'text-text')}>
            {fmtDate(phase.forecastStart)} to {fmtDate(phase.forecastEnd)}
          </span>
          {slip && <span className="block text-xs text-warn">Past the PA window</span>}
        </p>
      )}
    </Card>
  );
}

function HealthCard({ program, phase, canEdit }: { program: Program; phase: TrackerPhase; canEdit: boolean }) {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<HealthLevel>(phase.health.effective);
  const [reason, setReason] = useState('');
  const save = usePhaseSave(program, phase.id);
  const h = phase.health;
  return (
    <Card className="p-4 text-[13px]">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Health</h3>
        {canEdit && (
          <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
            <Pencil /> {h.overridden ? 'Change' : 'Override'}
          </Button>
        )}
      </div>
      <HealthBadge health={h} />
      {h.overridden ? (
        <p className="mt-2 text-xs text-muted">
          BlackWolf set this: “{h.overrideReason}”. The rules say {HEALTH_LABEL[h.computed].toLowerCase()}
          {h.reasons.length ? `: ${h.reasons.join('; ')}` : ''}.
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted">
          {h.reasons.length ? h.reasons.join('; ') : 'No late milestones, overdue tasks or critical issues.'}
        </p>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Health for Phase ${phase.number}`}
        description="An override is shown to the Steering Committee with your reason, next to what the rules compute."
        footer={
          <>
            {h.overridden && (
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={() => save.mutate({ healthOverride: null }, { onSuccess: () => setOpen(false) })}
              >
                Clear override
              </Button>
            )}
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={reason.trim().length < 3}
              onClick={() =>
                save.mutate(
                  { healthOverride: level, healthOverrideReason: reason.trim() },
                  { onSuccess: () => setOpen(false) },
                )
              }
            >
              Set health
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-[13px] text-muted">The rules currently say: {HEALTH_LABEL[h.computed]}.</p>
          <Field label="Health" htmlFor="h-level">
            <Select id="h-level" value={level} onChange={(e) => setLevel(e.target.value as HealthLevel)}>
              {(['GREEN', 'AMBER', 'RED'] as HealthLevel[]).map((l) => (
                <option key={l} value={l}>
                  {HEALTH_LABEL[l]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Reason" htmlFor="h-reason" hint="Required. Keep it to one line the committee can act on.">
            <Textarea
              id="h-reason"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </div>
      </Dialog>
    </Card>
  );
}

function MilestonesCard({
  program,
  phase,
  today,
  canEdit,
}: {
  program: Program;
  phase: TrackerPhase;
  today: string;
  canEdit: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<TrackerPhase['milestones'][number] | null>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: trackerKey(program.id) }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card className="p-4 text-[13px]">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Milestones</h3>
        {canEdit && !adding && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
            <Plus /> Add
          </Button>
        )}
      </div>
      <ul className="flex flex-col gap-1.5">
        {phase.milestones.map((m) => {
          const late = m.status === 'PLANNED' && m.forecastDate < today;
          return (
            <li key={m.id} className="flex items-center gap-2">
              <Diamond
                className={cn(
                  'size-3.5 shrink-0',
                  m.status === 'ACHIEVED' ? 'fill-ok text-ok' : late ? 'text-bad' : 'text-muted',
                )}
              />
              <span
                className={cn('min-w-0 flex-1 truncate', m.status === 'CANCELLED' && 'text-muted line-through')}
                title={m.name}
              >
                {m.name}
              </span>
              <span className={cn('text-xs tabular', late ? 'font-medium text-bad' : 'text-muted')}>
                {m.status === 'ACHIEVED'
                  ? `Done ${fmtDate(m.achievedDate)}`
                  : m.status === 'CANCELLED'
                    ? 'Cancelled'
                    : fmtDate(m.forecastDate)}
              </span>
              {canEdit && (
                <Button variant="ghost" size="icon" aria-label={`Edit ${m.name}`} onClick={() => setEditing(m)}>
                  <Pencil />
                </Button>
              )}
              {canEdit && m.status === 'PLANNED' && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Mark ${m.name} achieved`}
                  title="Mark achieved today"
                  onClick={() =>
                    mut.mutate(() =>
                      api.patch(`/api/programs/${program.id}/milestones/${m.id}`, { status: 'ACHIEVED' }),
                    )
                  }
                >
                  <Check />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {adding && (
        <form
          className="mt-2 flex flex-col gap-2"
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            mut.mutate(
              () =>
                api.post(`/api/programs/${program.id}/phases/${phase.id}/milestones`, {
                  name: f.get('n'),
                  plannedDate: f.get('d'),
                }),
              {
                onSuccess: () => setAdding(false),
              },
            );
          }}
        >
          <Input name="n" required placeholder="Milestone name" className="h-8 text-[13px]" />
          <div className="flex gap-2">
            <Input name="d" type="date" required aria-label="Date" className="h-8 text-[13px]" />
            <Button type="submit" size="sm">
              Add
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      {editing && (
        <MilestoneDialog
          m={editing}
          today={today}
          onClose={() => setEditing(null)}
          onSave={(body) =>
            mut.mutate(() => api.patch(`/api/programs/${program.id}/milestones/${editing.id}`, body), {
              onSuccess: () => {
                setEditing(null);
                toast.ok('Milestone saved.');
              },
            })
          }
          saving={mut.isPending}
        />
      )}
    </Card>
  );
}

const MILESTONE_STATUS = [
  ['PLANNED', 'Planned'],
  ['ACHIEVED', 'Achieved'],
  ['CANCELLED', 'Cancelled'],
] as const;

/** Rename, reforecast, mark achieved on a date, cancel or reinstate. The PA planned date stays. */
function MilestoneDialog({
  m,
  today,
  onClose,
  onSave,
  saving,
}: {
  m: TrackerPhase['milestones'][number];
  today: string;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(m.name);
  const [forecast, setForecast] = useState(m.forecastDate);
  const [status, setStatus] = useState<string>(m.status);
  const [achieved, setAchieved] = useState(m.achievedDate ?? today);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit milestone"
      description={`PA plan: ${fmtDate(m.plannedDate)}. The planned date stays; the forecast moves.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={saving}
            disabled={!name.trim() || !forecast || (status === 'ACHIEVED' && (!achieved || achieved > today))}
            onClick={() =>
              onSave({
                name: name.trim(),
                forecastDate: forecast,
                status,
                ...(status === 'ACHIEVED' ? { achievedDate: achieved } : {}),
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name" htmlFor="ms-name">
          <Input id="ms-name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Forecast date" htmlFor="ms-forecast">
          <Input id="ms-forecast" type="date" value={forecast} onChange={(e) => setForecast(e.target.value)} />
        </Field>
        <Field
          label="Status"
          htmlFor="ms-status"
          hint="A cancelled milestone stays on record, struck through, and no longer counts toward health."
        >
          <Select id="ms-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {MILESTONE_STATUS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
        {status === 'ACHIEVED' && (
          <Field label="Achieved on" htmlFor="ms-achieved" hint="Today or earlier.">
            <Input
              id="ms-achieved"
              type="date"
              max={today}
              value={achieved}
              onChange={(e) => setAchieved(e.target.value)}
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}

function PhaseTasks({ phase, tasks, data }: { phase: TrackerPhase; tasks: Task[]; data: TrackerData }) {
  const open = useOpenTask();
  const groups = [
    ...phase.workstreams.map((w) => ({
      key: w.id,
      label: w.name,
      tasks: tasks.filter((t) => t.workstreamId === w.id),
    })),
    {
      key: 'none',
      label: phase.workstreams.length ? 'No workstream' : '',
      tasks: tasks.filter((t) => !t.workstreamId),
    },
  ].filter((g) => g.tasks.length);
  return (
    <Card>
      {groups.map((g) => (
        <div key={g.key}>
          {g.label && (
            <h4 className="border-b border-border bg-surface-2/60 px-4 py-1.5 text-xs font-semibold">{g.label}</h4>
          )}
          <ul>
            {[...g.tasks]
              .sort((a, b) => (a.rank < b.rank ? -1 : 1))
              .map((t) => (
                <li key={t.id}>
                  <button
                    className="flex w-full items-center gap-3 border-b border-border px-4 py-2 text-left text-[13px] hover:bg-surface-2/50"
                    onClick={() => open(t.id)}
                  >
                    <StatusPill status={t.status} className="w-24 justify-center" />
                    <span className="min-w-0 flex-1 truncate">{t.title}</span>
                    <Avatar name={data.members.find((m) => m.userId === t.ownerId)?.name} size={20} />
                    <DueDate date={t.dueDate} overdue={isOverdue(t, data.today)} className="w-24 text-right text-xs" />
                  </button>
                </li>
              ))}
          </ul>
        </div>
      ))}
      {!groups.length && <p className="px-4 py-6 text-center text-sm text-muted">No tasks in this phase yet.</p>}
    </Card>
  );
}

function PhaseRaid({ program, phaseId }: { program: Program; phaseId: string }) {
  const q = useRaid(program.id, { phaseId });
  if (!q.data) return <Spinner />;
  return (
    <Card className="p-0">
      <p className="border-b border-border px-4 py-2 text-xs text-muted">
        Items raised against this phase. Program-wide items, including the PA §7 dependencies, are in the{' '}
        <Link to={`/p/${program.id}/raid`} className="text-brand-text hover:underline">
          RAID log
        </Link>
        .
      </p>
      <RaidTable program={program} items={q.data} showPhase={false} empty="No RAID items against this phase." />
    </Card>
  );
}

/** Markdown notes with autosave a moment after typing stops. */
function PhaseNotes({ program, phase, canEdit }: { program: Program; phase: TrackerPhase; canEdit: boolean }) {
  const [v, setV] = useState(phase.notes ?? '');
  const [state, setState] = useState<'saved' | 'dirty' | 'saving'>('saved');
  const save = usePhaseSave(program, phase.id);
  useEffect(() => {
    if (state !== 'dirty') return;
    const t = setTimeout(() => {
      setState('saving');
      save.mutate({ notes: v || null }, { onSuccess: () => setState('saved'), onError: () => setState('dirty') });
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v, state]);

  if (!canEdit) {
    return (
      <Card className="prose-helm p-5 text-[13.5px]">
        {phase.notes ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{phase.notes}</ReactMarkdown>
        ) : (
          <p className="text-muted">No notes yet.</p>
        )}
      </Card>
    );
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <Textarea
          aria-label="Phase notes"
          rows={18}
          value={v}
          onChange={(e) => {
            setV(e.target.value);
            setState('dirty');
          }}
          className="font-mono text-[12.5px]"
          placeholder="Markdown supported. Saves as you type."
        />
        <p className="mt-1 text-xs text-muted">
          {state === 'saved' ? 'Saved' : state === 'saving' ? 'Saving…' : 'Unsaved changes'}
        </p>
      </div>
      <Card className="prose-helm p-5 text-[13.5px]">
        {v ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{v}</ReactMarkdown> : <p className="text-muted">Preview</p>}
      </Card>
    </div>
  );
}
